import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, FormikInput, Headline, useDismissableErrorMessage } from '../../../../common/ui';
import * as Yup from 'yup';
import accountNumber from '../../../../common/validation/no/accountNumber';
import style from './style.module.scss';
import { SignCommonComponent, useApplicationSignContext } from '../../../../common/components/Sign/SignCommon';
import { FormikConfig, Form } from 'formik';
import { pollForExpectedApplicationState, setAccountNumber } from '../../../../common/services/legacyApplicationApiService';
import { ApplicationPaths, navigateToStep } from '../../../../common/applicationPages/ApplicationFormsHelpers';
import { ApplicationStatusModel } from '../../../../common/serverGeneratedTypings/applicationStatusModel';
import { useApplicationIdContext } from '../../../../common/components/ApplicationContext';
import { trackSectionView } from '../../../../common/lib/gtm';
import ValidationFeedbackFormik from '../../../../common/components/ValidationFeedbackFormik';
import { useTranslation } from 'react-i18next';
import { navigateToCorrectStep } from '../../App';
import { startSign } from './Sign';
import { SubmitProgressBox } from '@n2-common/ui/SubmitProgress';
import { SubmitState } from '@n2-common/components/MultiStageOnePageWizard/types';
import { createRetryAction, errorToAction } from '@n2-common/applicationPages/applicationErrorHelper';
import { logErrorToServerside } from '@n2-common/services/serverSideLogging';

interface AccountNumberForm {
    accountNumber: string;
}

export function SetAccountNumber(): JSX.Element {
    return (
        <SignCommonComponent navigateToCorrectStepFunc={navigateToCorrectStep}>
            <SetAccountNumberInner />
        </SignCommonComponent>
    );
}
const formname = 'no-accountnumber';

function SetAccountNumberInner(): JSX.Element {
    const { t } = useTranslation(['common', 'validation']);
    const navigate = useNavigate();
    const { setErrorMessage, ErrorOutput } = useDismissableErrorMessage();

    const [submitState, setSubmitState] = useState<SubmitState>('NotSent');

    const { applicationStatus, applicantId, applicationId, applicationSignStatus } = useApplicationSignContext();
    const [submitting, setSubmitting] = useState(false);

    const { country, productType } = useApplicationIdContext();

    useEffect(() => {
        trackSectionView(productType, country, '4.1', 'Set account number');
    }, [country, productType]);

    const validationSchema = Yup.object().shape({
        accountNumber: accountNumber(),
    });

    const accountNumberForm = useMemo(() => {
        if (!applicationStatus || !applicationSignStatus) {
            return <div></div>;
        }

        if (applicationSignStatus.MainApplicantHasSigned || applicationSignStatus.CoApplicantHasSigned) {
            return (
                <div>
                    <h3>{t<string>('Sign.AccountNumber.CannotChangeWhenSigned')}</h3>
                    <div>{applicationStatus.AccountNumberForPayout}</div>
                </div>
            );
        }
        const setAccountNumberFunc = async (accountNumber: string) => {
            if (accountNumber.length === 0) {
                return;
            }
            setSubmitting(true);
            setSubmitState('Submitted');
            let status: ApplicationStatusModel | undefined = undefined;
            try {
                await setAccountNumber(accountNumber, applicationId, applicantId);
                status = await pollForExpectedApplicationState(
                    applicationId,
                    applicantId,
                    (res: ApplicationStatusModel) => res.AccountNumberForPayout === accountNumber
                );
            } catch (err) {
                const action = errorToAction(err, formname, applicationId, applicantId);
                if (action) {
                    setErrorMessage(action);
                } else {
                    logErrorToServerside('Error setting accountnumber', applicationId, applicantId, err);
                    setErrorMessage(createRetryAction(t, formname));
                }
                setSubmitState('Error');
            }
            setSubmitting(false);
            if (status) {
                if (await startSign(applicationId, applicantId, setSubmitState)) {
                    return;
                }
            }
            navigateToStep(ApplicationPaths.sign, applicationId, navigate, applicantId);
        };

        const config: FormikConfig<AccountNumberForm> = {
            initialValues: {
                accountNumber: applicationStatus.AccountNumberForPayout,
            },
            validationSchema,
            onSubmit: async (values) => {
                setAccountNumberFunc(values.accountNumber);
            },
        };

        return (
            <>
                <ValidationFeedbackFormik {...config}>
                    <Form className={style.form} name={formname} data-matomo-name={'signdetails'}>
                        <FormikInput  id="accountNumber" name="accountNumber" label={t<string>('Sign.AccountNumber.InputLabel')} type="text"></FormikInput>
                        <Button disabled={submitting} type="submit">
                            {t<string>('Sign.AccountNumber.Save')}
                        </Button>
                        <SubmitProgressBox
                            state={submitState}
                            submitText={t<string>('FormSubmitButton.Submitted')}
                            processingText={t<string>('FormSubmitButton.Processing')}
                        ></SubmitProgressBox>
                    </Form>
                </ValidationFeedbackFormik>
                <ErrorOutput />
            </>
        );
    }, [
        applicationStatus,
        applicationSignStatus,
        validationSchema,
        t,
        submitting,
        submitState,
        ErrorOutput,
        applicationId,
        navigate,
        applicantId,
        setErrorMessage,
    ]);

    return (
        <>
            <span className={style.backButton} onClick={() => navigateToStep(ApplicationPaths.sign, applicationId, navigate, applicantId)}>
                {' '}
                {'< '}
                {t<string>('Navigation.Back')}{' '}
            </span>
            <div className={style.wrapper}>
                <Headline>{t<string>('Sign.AccountNumber.Headline')}</Headline>
                {accountNumberForm}
            </div>
        </>
    );
}
